import { AxiosError } from 'axios';

import {
  CatalogueAnswers,
  CatalogueAnswersNames,
  CatalogueQuestions,
  CatalogueType,
  CatalogueWorksheetPage,
  FieldRow,
  FieldUpsertInput,
  FieldUpsertResponse,
  PreviewInput,
  PreviewResponse,
  StyleRegistry,
  SuggestionDismissResponse,
  WorksheetPassSettings,
  WorksheetUpsertInput,
  WorksheetUpsertResponse,
} from '@/types/worksheetPass';

import api from './axiosInstance';
import { worksheetPassStylesFixture } from './fixtures/worksheetPassStyles';

const WORKSHEET_PASS_API_URL =
  'https://api.vh3connect.io/api:f-Fs4_ed/worksheet-pass';

export const CATALOGUE_PAGE_SIZE = 100;

// Dev only: serve the styles fixture while the FSI routes behind
// `GET worksheet-pass/styles` are not deployed. Never on in a build.
const USE_STYLES_FIXTURE =
  import.meta.env.DEV &&
  import.meta.env.VITE_WORKSHEET_PASS_STYLES_FIXTURE === 'true';

// Xano returns plain sentences meant for the admin, so the message is shown as written.
export class WorksheetPassApiError extends Error {
  status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'WorksheetPassApiError';
    this.status = status;
  }
}

const toApiError = (error: unknown): WorksheetPassApiError => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { message?: unknown } | undefined;
    const message =
      typeof data?.message === 'string' && data.message.trim()
        ? data.message
        : 'Something went wrong. Try again in a moment.';
    return new WorksheetPassApiError(message, error.response?.status ?? null);
  }
  if (error instanceof Error)
    return new WorksheetPassApiError(error.message, null);
  return new WorksheetPassApiError('Something went wrong.', null);
};

const request = async <T>(fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    throw toApiError(error);
  }
};

const url = (path: string) => `${WORKSHEET_PASS_API_URL}/${path}`;

export const worksheetPassApi = {
  getSettings: () =>
    request(() => api.get<WorksheetPassSettings>(url('settings'))),

  setSwitch: (enabled: boolean) =>
    request(() =>
      api.post<{ worksheet_pass: boolean }>(url('settings/switch'), { enabled })
    ),

  getCatalogueWorksheets: (params: {
    name?: string;
    type?: CatalogueType;
    pageNumber: number;
    pageSize?: number;
  }) =>
    request(() =>
      api.get<CatalogueWorksheetPage>(url('catalogue/worksheets'), {
        params: {
          name: params.name || undefined,
          type: params.type,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize ?? CATALOGUE_PAGE_SIZE,
        },
      })
    ),

  getCatalogueQuestions: (formRef: string) =>
    request(() =>
      api.get<CatalogueQuestions>(url('catalogue/questions'), {
        params: { form_ref: formRef },
      })
    ),

  getCatalogueAnswers: (
    formRef: string,
    fieldRef: string,
    names?: CatalogueAnswersNames
  ) =>
    request(() =>
      api.get<CatalogueAnswers>(url('catalogue/answers'), {
        params: {
          form_ref: formRef,
          field_ref: fieldRef,
          form_name: names?.formName || undefined,
          question: names?.question || undefined,
        },
      })
    ),

  upsertWorksheet: (input: WorksheetUpsertInput) =>
    request(() =>
      api.post<WorksheetUpsertResponse>(url('worksheets/upsert'), input)
    ),

  getWorksheetFields: (formRef: string) =>
    request(() =>
      api.get<{ form_ref: string; fields: FieldRow[] }>(
        url('worksheets/fields'),
        { params: { form_ref: formRef } }
      )
    ),

  upsertField: (input: FieldUpsertInput) =>
    request(() => api.post<FieldUpsertResponse>(url('fields/upsert'), input)),

  getStyles: async (): Promise<StyleRegistry> => {
    if (USE_STYLES_FIXTURE) return worksheetPassStylesFixture;
    return request(() => api.get<StyleRegistry>(url('styles')));
  },

  dismissSuggestion: (formRef: string, fieldRef: string) =>
    request(() =>
      api.post<SuggestionDismissResponse>(url('suggestions/dismiss'), {
        form_ref: formRef,
        field_ref: fieldRef,
      })
    ),

  preview: (input: PreviewInput) =>
    request(() => api.post<PreviewResponse>(url('preview'), input)),
};
